import Vue from "vue";
import {
  getCachedPrintSettings,
  getPrintSettings,
  savePrintSettings,
  clearPrintSettings,
  initPrintSettingsCache,
} from "@/util/indexDb/printSettingsIndexDb";

const SESSION_EXEMPT_PRINT_TTYPES = ["test_print", "empire-login-ticket"];
const MAX_PRINT_URL_LENGTH = 4096;
let printAuthDeniedLoggedAt = 0;

function hasActivePosSession() {
  try {
    const token = window.localStorage.getItem("JWT_TOKEN");
    if (typeof token !== "string" || !token.trim()) return false;
    const parts = token.trim().replace(/^Bearer\s+/i, "").split(".");
    return (
      parts.length === 3 &&
      parts.every((part) => /^[A-Za-z0-9_-]+$/.test(part))
    );
  } catch (err) {
    console.error("session lookup failed", err);
    return false;
  }
}

function isSafePrintDataUrl(url) {
  if (typeof url !== "string" || !url) return false;
  if (url.length > MAX_PRINT_URL_LENGTH) return false;
  if (/\s/.test(url)) return false;
  return /^https?:\/\//i.test(url);
}

export const PRINT_DENIAL = {
  INVALID_URL: "invalid_print_url",
  NO_SESSION: "no_active_session",
};

const PRINT_DENIAL_MESSAGES = {
  [PRINT_DENIAL.INVALID_URL]:
    "Print blocked: invalid print address. Please contact support.",
  [PRINT_DENIAL.NO_SESSION]: "Print blocked. Please login again.",
};

export function getPrintDenialReason(payload) {
  try {
    if (
      payload &&
      payload.ttype === "dynamicURL" &&
      !isSafePrintDataUrl(payload.dataURL)
    ) {
      return PRINT_DENIAL.INVALID_URL;
    }
    if (
      payload &&
      payload.ttype &&
      SESSION_EXEMPT_PRINT_TTYPES.indexOf(payload.ttype) !== -1
    ) {
      return "";
    }
    return hasActivePosSession() ? "" : PRINT_DENIAL.NO_SESSION;
  } catch (err) {
    console.error("print authorization check failed", err);
    return PRINT_DENIAL.NO_SESSION;
  }
}

export function isPrintEmitAuthorized(payload) {
  const reason = getPrintDenialReason(payload);
  if (!reason) return true;
  notifyPrintDenied(reason, payload && payload.ttype);
  return false;
}

function notifyPrintDenied(reason, ttype) {
  console.error(
    "print emit blocked: " + reason + (ttype ? " (ttype=" + ttype + ")" : "")
  );
  const now = Date.now();
  if (now - printAuthDeniedLoggedAt <= 5000) return;
  printAuthDeniedLoggedAt = now;
  try {
    if (Vue.toasted && typeof Vue.toasted.show === "function") {
      Vue.toasted.show(
        PRINT_DENIAL_MESSAGES[reason] || PRINT_DENIAL_MESSAGES[PRINT_DENIAL.NO_SESSION]
      );
    }
  } catch (err) {
    console.error("print denied toast failed", err);
  }
}

export const PRINT_KIND = {
  TICKET: "ticket",
  RECEIPT: "receipt",
};

const RECEIPT_TTYPES = [
  "fnb",
  "boxfnb",
  "cash_card_receipt",
  "fnb-refund",
  "fnb-security",
  "pos_collection_summary",
  "tim_card_holder_receipt",
];

const SKIP_RECEIPT_FOLLOWUP_TTYPES = RECEIPT_TTYPES.concat([
  "addon-ticket",
  "security-ticket",
  "security-ticket-empire",
  "empire-login-ticket",
  "empire-new-ticket",
  "test_print",
  "refund-invoice",
  "shift-manager-login",
]);

export {
  getPrintSettings,
  savePrintSettings,
  clearPrintSettings,
  initPrintSettingsCache,
  getCachedPrintSettings,
};

export function getPosPrintSettings() {
  return getCachedPrintSettings();
}

export function setPosPrintSettings(settings) {
  return savePrintSettings(settings);
}

function hasValue(value) {
  return value != null && String(value).trim() !== "";
}

function isSafeTemplateId(value) {
  return hasValue(value) && /^[A-Za-z0-9_-]+$/.test(String(value).trim());
}

export function getSelectedTemplateId(fallback) {
  const settings = getCachedPrintSettings();
  if (hasValue(settings.template_id)) {
    return settings.template_id;
  }
  return fallback != null ? fallback : null;
}

export function getSelectedReceiptTemplateId(fallback) {
  const settings = getCachedPrintSettings();
  if (hasValue(settings.receipt_template_id)) {
    return settings.receipt_template_id;
  }
  return fallback != null ? fallback : null;
}

export function hasTicketPrintOverride() {
  const settings = getCachedPrintSettings();
  return (
    hasValue(settings.template_id) || hasValue(settings.ticket_printer_name)
  );
}

export function hasReceiptPrintOverride() {
  const settings = getCachedPrintSettings();
  return (
    hasValue(settings.receipt_template_id) ||
    hasValue(settings.receipt_printer_name)
  );
}

export function shouldPrintSeparateReceipt() {
  return hasReceiptPrintOverride();
}

export function getPrinterDeviceName(kind) {
  const settings = getCachedPrintSettings();
  if (kind === PRINT_KIND.RECEIPT) {
    return hasValue(settings.receipt_printer_name)
      ? String(settings.receipt_printer_name).trim()
      : "";
  }
  return hasValue(settings.ticket_printer_name)
    ? String(settings.ticket_printer_name).trim()
    : "";
}

export function getTemplateIdForKind(kind) {
  const settings = getCachedPrintSettings();
  if (kind === PRINT_KIND.RECEIPT) {
    return hasValue(settings.receipt_template_id)
      ? settings.receipt_template_id
      : null;
  }
  return hasValue(settings.template_id) ? settings.template_id : null;
}

function appendQueryParams(url, params) {
  if (!url || !params) return url;
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (hasValue(value)) {
      parts.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      );
    }
  });
  if (!parts.length) return url;
  const joiner = String(url).indexOf("?") >= 0 ? "&" : "?";
  return `${url}${joiner}${parts.join("&")}`;
}

function stripQueryParam(url, key) {
  if (!url || !key) return url;
  const raw = String(url);
  const qIndex = raw.indexOf("?");
  if (qIndex < 0) return raw;
  const base = raw.slice(0, qIndex);
  const search = raw.slice(qIndex + 1);
  const kept = search
    .split("&")
    .filter((part) => {
      if (!part) return false;
      const rawName = part.split("=")[0] || "";
      let name = rawName;
      try {
        name = decodeURIComponent(rawName);
      } catch (err) {
        name = rawName;
      }
      return name !== key;
    })
    .join("&");
  return kept ? `${base}?${kept}` : base;
}

function isVoucherPrintUrl(url) {
  const value = String(url || "").toLowerCase();
  return (
    value.indexOf("dynamic-voucher") >= 0 ||
    value.indexOf("voucher-print") >= 0 ||
    value.indexOf("qrail-voucher") >= 0
  );
}

function isCinemaTicketUrl(url) {
  const value = String(url || "").toLowerCase();
  return (
    value.indexOf("boplusfnb-ticket") >= 0 ||
    value.indexOf("print-bo-fnb-ticket") >= 0 ||
    value.indexOf("generate-ticket") >= 0 ||
    value.indexOf("generate-single-ticket") >= 0 ||
    value.indexOf("generate-entrance-ticket") >= 0 ||
    value.indexOf("generate-empire-ticket") >= 0 ||
    value.indexOf("generate-refund-ticket") >= 0 ||
    value.indexOf("generate-security-ticket") >= 0 ||
    value.indexOf("/generate-receipt/") >= 0 ||
    value.indexOf("novo-print-ticket") >= 0
  );
}

function isReceiptPrintUrl(url) {
  const value = String(url || "").toLowerCase();
  return (
    value.indexOf("generate-fnb") >= 0 ||
    value.indexOf("cash_card_receipt") >= 0 ||
    value.indexOf("premier_item_receipt") >= 0 ||
    value.indexOf("shiftcloseinvoicesummary") >= 0
  );
}

export function resolvePrintKind(payload) {
  if (!payload || typeof payload !== "object") return PRINT_KIND.TICKET;
  if (
    payload.printKind === PRINT_KIND.RECEIPT ||
    payload.printKind === PRINT_KIND.TICKET
  ) {
    return payload.printKind;
  }
  if (payload.ttype && RECEIPT_TTYPES.indexOf(payload.ttype) !== -1) {
    return PRINT_KIND.RECEIPT;
  }
  if (payload.ttype === "dynamicURL") {
    if (isReceiptPrintUrl(payload.dataURL) && !isCinemaTicketUrl(payload.dataURL)) {
      return PRINT_KIND.RECEIPT;
    }
    return PRINT_KIND.TICKET;
  }
  return PRINT_KIND.TICKET;
}

function isTicketStylePrint(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (payload.__skipReceiptFollowup) return false;
  if (payload.printKind === PRINT_KIND.RECEIPT) return false;
  if (payload.ttype && SKIP_RECEIPT_FOLLOWUP_TTYPES.indexOf(payload.ttype) !== -1) {
    return false;
  }
  if (isVoucherPrintUrl(payload.dataURL)) return false;
  if (payload.ttype === "dynamicURL") {
    return isCinemaTicketUrl(payload.dataURL);
  }
  return !!(payload.showBookingId || payload.sb_id);
}

function isPrintHtmlUrl(url) {
  const value = String(url || "").toLowerCase();
  return (
    isCinemaTicketUrl(value) ||
    value.indexOf("generate-fnb") >= 0 ||
    value.indexOf("generate-refund") >= 0 ||
    value.indexOf("generate-addon-ticket") >= 0 ||
    value.indexOf("generate-security-ticket") >= 0 ||
    value.indexOf("cash_card_receipt") >= 0 ||
    value.indexOf("premier_item_receipt") >= 0 ||
    value.indexOf("shiftcloseinvoicesummary") >= 0
  );
}

export function applyPrintSettingsToHttpUrl(url) {
  if (typeof url !== "string" || !url) return url;
  if (!isPrintHtmlUrl(url)) return url;
  const kind = isCinemaTicketUrl(url) ? PRINT_KIND.TICKET : PRINT_KIND.RECEIPT;
  const templateId = getTemplateIdForKind(kind);
  if (!isSafeTemplateId(templateId)) return url;
  if (url.indexOf("template_id=") >= 0) return url;
  return appendQueryParams(url, { template_id: String(templateId).trim() });
}

export function enrichPrintPayload(payload) {
  if (!payload || typeof payload !== "object") return payload;

  const kind = resolvePrintKind(payload);
  const deviceName = getPrinterDeviceName(kind);
  const templateId = getTemplateIdForKind(kind);
  const needsDevice = !!(deviceName && !payload.deviceName);
  const needsTemplate = isSafeTemplateId(templateId);

  if (!needsDevice && !needsTemplate) {
    return payload;
  }

  const next = Object.assign({}, payload);

  if (needsDevice) {
    next.deviceName = deviceName;
  }

  if (needsTemplate) {
    const extra = Object.assign({}, next.extra_query_obj || {});
    if (!hasValue(extra.template_id)) {
      extra.template_id = templateId;
    }
    next.extra_query_obj = extra;
    next.template_id = extra.template_id;
    const reprintRaw =
      next.isReprint != null ? String(next.isReprint) : "false";
    if (reprintRaw.indexOf("template_id=") < 0) {
      next.isReprint = `${reprintRaw}&template_id=${String(
        extra.template_id
      ).trim()}`;
    }

    if (next.dataURL) {
      next.dataURL = appendQueryParams(stripQueryParam(next.dataURL, "template_id"), {
        template_id: extra.template_id,
      });
    }
  }

  return next;
}

function buildReceiptFollowupPayload(payload) {
  const next = Object.assign({}, payload, {
    printKind: PRINT_KIND.RECEIPT,
    __skipReceiptFollowup: true,
  });
  delete next.deviceName;
  delete next.template_id;
  if (typeof next.isReprint === "string" && next.isReprint.indexOf("&template_id=") >= 0) {
    next.isReprint = next.isReprint.split("&template_id=")[0];
  }
  if (next.extra_query_obj) {
    next.extra_query_obj = Object.assign({}, next.extra_query_obj);
    delete next.extra_query_obj.template_id;
  }
  if (next.ttype === "dynamicURL" && next.dataURL) {
    next.dataURL = stripQueryParam(next.dataURL, "template_id");
  }
  return enrichPrintPayload(next);
}

const PRINT_QUEUE_GAP_MS = 1800;
const PRINT_QUEUE_MAX = 20;
const PRINT_JOB_TTL_MS = 45000;
let printQueue = [];
let printQueueRunning = false;
let printQueueTimer = null;

function hasSufficientPrintData(data) {
  if (!data || typeof data !== "object") return false;
  if (data.ttype === "dynamicURL") {
    return typeof data.dataURL === "string" && data.dataURL.length > 8;
  }
  if (data.ttype) return true;
  return !!(data.showBookingId && data.baseurl && data.cinema_id);
}

function isSocketReady(socket) {
  if (!socket) return false;
  if (socket.connected === false) return false;
  return typeof socket.emit === "function";
}

function isJobExpired(job) {
  return !job || Date.now() - job.enqueuedAt > PRINT_JOB_TTL_MS;
}

function releasePrintJob(job) {
  if (!job) return;
  job.originalEmit = null;
  job.ctx = null;
  job.data = null;
  job.event = null;
}

function clearPrintQueue() {
  while (printQueue.length) {
    releasePrintJob(printQueue.shift());
  }
  printQueue = [];
  printQueueRunning = false;
  if (printQueueTimer) {
    clearTimeout(printQueueTimer);
    printQueueTimer = null;
  }
}

function prunePrintQueue() {
  const now = Date.now();
  for (let i = printQueue.length - 1; i >= 0; i--) {
    const job = printQueue[i];
    if (!job || now - job.enqueuedAt > PRINT_JOB_TTL_MS) {
      releasePrintJob(job);
      printQueue.splice(i, 1);
    }
  }
  while (printQueue.length > PRINT_QUEUE_MAX) {
    releasePrintJob(printQueue.shift());
  }
  if (printQueue.length > PRINT_QUEUE_MAX) {
    clearPrintQueue();
  }
}

function scheduleNextDrain(delayMs) {
  if (printQueueTimer) {
    clearTimeout(printQueueTimer);
  }
  printQueueTimer = setTimeout(() => {
    printQueueTimer = null;
    printQueueRunning = false;
    if (printQueue.length > PRINT_QUEUE_MAX) {
      clearPrintQueue();
      return;
    }
    drainPrintQueue();
  }, delayMs);
}

function drainPrintQueue() {
  if (printQueueRunning) return;
  prunePrintQueue();
  if (!printQueue.length) return;
  printQueueRunning = true;
  const job = printQueue[0];
  if (isJobExpired(job)) {
    printQueue.shift();
    console.error("print job expired before socket became ready");
    releasePrintJob(job);
    scheduleNextDrain(0);
    return;
  }
  if (!isSocketReady(job.ctx)) {
    scheduleNextDrain(PRINT_QUEUE_GAP_MS);
    return;
  }
  printQueue.shift();
  try {
    job.originalEmit.call(job.ctx, job.event, job.data);
  } catch (err) {
    console.error("print queue emit failed", err);
  } finally {
    releasePrintJob(job);
  }
  scheduleNextDrain(PRINT_QUEUE_GAP_MS);
}

function enqueuePrintJob(originalEmit, ctx, event, data) {
  if (!data || typeof data !== "object") return false;
  if (!hasSufficientPrintData(data)) {
    console.warn("print payload missing expected fields", {
      ttype: data.ttype,
      hasDataURL: !!data.dataURL,
      hasShowBookingId: !!data.showBookingId,
    });
  }
  prunePrintQueue();
  if (printQueue.length >= PRINT_QUEUE_MAX) {
    releasePrintJob(printQueue.shift());
  }
  if (printQueue.length >= PRINT_QUEUE_MAX) {
    clearPrintQueue();
    return false;
  }
  printQueue.push({
    originalEmit,
    ctx,
    event,
    data,
    enqueuedAt: Date.now(),
  });
  drainPrintQueue();
  return true;
}

function emitPrintThrough(originalEmit, event, data) {
  if (!isPrintEmitAuthorized(data)) {
    return false;
  }
  let enriched = data;
  let followup = null;
  try {
    enriched = enrichPrintPayload(data);
    if (shouldPrintSeparateReceipt() && isTicketStylePrint(data)) {
      followup = buildReceiptFollowupPayload(enriched);
    }
  } catch (err) {
    console.error("print payload enrichment failed, sending original", err);
    enriched = data;
    followup = null;
  }
  const queued = enqueuePrintJob(originalEmit, this, event, enriched);
  if (queued && followup) {
    enqueuePrintJob(originalEmit, this, event, followup);
  }
  return queued;
}

let printEmitPatchInstalled = false;

export function emitPosPrintTicket(socket, payload) {
  if (!socket || typeof socket.emit !== "function") return false;
  const data = enrichPrintPayload(payload && typeof payload === "object" ? payload : {});
  return socket.emit("on_print_ticket", data);
}

export function patchSocketPrintEmit(socket) {
  if (!socket || typeof socket.emit !== "function") return socket;
  if (socket.__posPrintSettingsPatched) return socket;
  const originalEmit = socket.emit.bind(socket);
  socket.emit = function (event, data) {
    if (event === "on_print_ticket" && data && typeof data === "object") {
      return emitPrintThrough.call(socket, originalEmit, event, data);
    }
    return originalEmit.apply(socket, arguments);
  };
  socket.__posPrintSettingsPatched = true;
  return socket;
}

export function installGlobalPrintEmitPatch() {
  if (printEmitPatchInstalled) return;
  if (typeof window !== "undefined" && window.__posPrintEmitProtoPatched) {
    printEmitPatchInstalled = true;
    return;
  }
  try {
    const SocketCtor = require("socket.io-client/lib/socket");
    if (
      SocketCtor &&
      SocketCtor.prototype &&
      !SocketCtor.prototype.__posPrintSettingsPatched
    ) {
      const originalEmit = SocketCtor.prototype.emit;
      SocketCtor.prototype.emit = function (event, data) {
        if (this && this.__posPrintSettingsPatched) {
          return originalEmit.apply(this, arguments);
        }
        if (event === "on_print_ticket" && data && typeof data === "object") {
          return emitPrintThrough.call(this, originalEmit, event, data);
        }
        return originalEmit.apply(this, arguments);
      };
      SocketCtor.prototype.__posPrintSettingsPatched = true;
      printEmitPatchInstalled = true;
      if (typeof window !== "undefined") {
        window.__posPrintEmitProtoPatched = true;
      }
    }
  } catch (err) {
    console.error("print emit proto patch failed", err);
  }
}



// WEBPACK FOOTER //
// ./src/helper/posPrintSettings.js